// src/pages/Cloud/components/fsc/FSCInlineSingleEditFromTable.js
import React, { forwardRef, useImperativeHandle, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Menu, MenuItem, Modal, Button } from '@mui/material';

import toastAlert from '../../../../js/Base/ToastAlert';
import {
  bulkEditFSCClauses,
  removeFSCControl,
} from '../../../../store/slices/fscComplianceSlice';

import FSCEditClauseItemSidebar from './FSCEditClauseItemSidebar';
import { buildNodeIndex, findFscRootInControls, normalizeTreeEnsureId } from './fscEditClauseUtils';
import { refreshFscProjectData } from './useFscProjectBootstrap';

/**
 * 只處理「FSC 單筆編輯」的 UI 與資料流：
 * - 由表格觸發 openMenu
 * - menu 只提供 編輯 / 刪除
 * - 編輯：開 FSCEditClauseItemSidebar（tree 僅使用 FSC 那棵）
 * - 確定：bulkEditFSCClauses（只針對當月）→ dispatch 刷新，不 reload
 */
const FSCInlineSingleEditFromTable = forwardRef(function FSCInlineSingleEditFromTable(
  { controls = [], projectUuid = null, fscCloud = null, cloudScope = null, effectiveCurrentMonthKey = null },
  ref
) {
  const dispatch = useDispatch();

  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [menuNode, setMenuNode] = useState(null);

  const [showEditSidebar, setShowEditSidebar] = useState(false);
  const [editingLeafPk, setEditingLeafPk] = useState(null);
  const [editingInitialStatus, setEditingInitialStatus] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const fscTreeOnly = useMemo(() => {
    const fscRoot = findFscRootInControls(controls);
    if (!fscRoot) return [];

    // 關鍵：只把 FSC 的 children（實際條款樹）給 Sidebar
    return normalizeTreeEnsureId(fscRoot.children || []);
  }, [controls]);

  const fscNodeIndex = useMemo(() => buildNodeIndex(fscTreeOnly), [fscTreeOnly]);

  useImperativeHandle(
    ref,
    () => ({
      openMenu(event, payload) {
        if (!event) return;
        event.stopPropagation();

        const node = payload?.node ?? payload ?? null;
        if (node?.read_only) {
          setMenuAnchorEl(null);
          setMenuNode(null);
          return;
        }

        setMenuAnchorEl(event.currentTarget);
        setMenuNode(node);
      },
    }),
    []
  );

  const closeMenu = () => {
    setMenuAnchorEl(null);
  };

  const openEdit = () => {
    if (menuNode?.read_only) {
      toastAlert.autoClose(true).showError('未分派範圍的舊條款僅供檢視，無法編輯');
      closeMenu();
      return;
    }
    const leafPk = menuNode?.id ?? menuNode?.uuid ?? null;

    // 從 table node 的當月格子取 raw_status（passed/partial/failed）
    const monthKey = effectiveCurrentMonthKey;
    const raw = monthKey && menuNode?.assessments?.[0]?.monthly_status?.[monthKey]?.raw_status;

    const nextStatus = raw ? String(raw).toLowerCase() : null;

    setEditingLeafPk(leafPk);
    setEditingInitialStatus(nextStatus);
    setShowEditSidebar(true);
    closeMenu();
  };

  const closeEdit = () => {
    if (submitting) return;
    setShowEditSidebar(false);
    setEditingLeafPk(null);
    setEditingInitialStatus(null);
  };

  const openDelete = () => {
    if (menuNode?.read_only) {
      toastAlert.autoClose(true).showError('未分派範圍的舊條款僅供檢視，無法刪除');
      closeMenu();
      return;
    }
    setShowDeleteConfirm(true);
    closeMenu();
  };

  const closeDelete = () => {
    if (deleting) return;
    setShowDeleteConfirm(false);
  };

  /** 儲存/刪除後：await 樹、assessments、合規摘要更新完再顯示成功 */
  const refreshFsc = async () => {
    await refreshFscProjectData(dispatch, projectUuid, fscCloud, {
      unwrap: true,
      platform: fscCloud,
      cloudScope,
    });
  };

  const handleSave = async ({ leafPk, edits }) => {
    if (!projectUuid) {
      toastAlert.autoClose(true).showError('缺少 project_uuid');
      return;
    }
    if (!effectiveCurrentMonthKey) {
      toastAlert.autoClose(true).showError('缺少當月月份資訊');
      return;
    }

    if (!fscCloud) {
      toastAlert.autoClose(true).showError('缺少 cloud 參數');
      return;
    }

    const record = fscNodeIndex.byPk.get(leafPk);
    const leafNode = record?.node || null;
    const pathNodes = record?.path || [];

    if (!leafNode) {
      toastAlert.autoClose(true).showError('找不到條款節點，請重新整理後再試');
      return;
    }
    if (leafNode.read_only) {
      toastAlert.autoClose(true).showError('未分派範圍的舊條款僅供檢視，無法編輯');
      return;
    }

    const draft = {
      key: leafPk,
      leafPk,
      leafNode,
      pathNodes,
      edits: {
        control_name: edits?.control_name,
        status: edits?.status ?? null,
        recommendation: edits?.recommendation ?? '',
        remediation_steps: edits?.remediation_steps ?? '',
      },
    };

    setSubmitting(true);
    try {
      await dispatch(
        bulkEditFSCClauses({
          projectUuid,
          cloud: fscCloud,
          cloud_scope: cloudScope,
          monthKey: effectiveCurrentMonthKey,
          drafts: [draft],
        })
      ).unwrap();

      await refreshFsc();

      toastAlert.autoClose(true).showSuccess('編輯成功');
      setShowEditSidebar(false);
      setEditingLeafPk(null);
    } catch (err) {
      const msg = typeof err === 'string' ? err : err?.detail || err?.message || '編輯條款失敗';
      toastAlert.autoClose(true).showError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const confirmDelete = async () => {
    if (!projectUuid) {
      toastAlert.autoClose(true).showError('缺少 project_uuid');
      return;
    }
    if (!fscCloud) {
      toastAlert.autoClose(true).showError('缺少 cloud 參數');
      return;
    }
    if (menuNode?.read_only) {
      toastAlert.autoClose(true).showError('未分派範圍的舊條款僅供檢視，無法刪除');
      setShowDeleteConfirm(false);
      return;
    }
    const controlUuid = menuNode?.uuid ?? null;
    if (!controlUuid) {
      toastAlert.autoClose(true).showError('缺少 control_uuid，無法刪除');
      return;
    }

    setDeleting(true);
    try {
      await dispatch(
        removeFSCControl({
          control_uuid: controlUuid,
          project_uuid: projectUuid,
          cloud: fscCloud,
          cloud_scope: cloudScope,
        })
      ).unwrap();
      await refreshFsc();
      toastAlert.autoClose(true).showSuccess('刪除成功');
      setShowDeleteConfirm(false);
      setMenuNode(null);
    } catch (err) {
      const msg = typeof err === 'string' ? err : err?.detail || err?.message || '刪除控制項失敗';
      toastAlert.autoClose(true).showError(msg);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={closeMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem onClick={openEdit} disabled={Boolean(menuNode?.read_only)}>
          編輯
        </MenuItem>
        <MenuItem onClick={openDelete} disabled={Boolean(menuNode?.read_only)}>
          刪除
        </MenuItem>
      </Menu>

      <FSCEditClauseItemSidebar
        show={showEditSidebar}
        onHide={closeEdit}
        tree={fscTreeOnly}
        initialLeafPk={editingLeafPk || null}
        initialDraft={null}
        initialStatus={editingInitialStatus}
        onSave={handleSave}
      />

      <Modal open={showDeleteConfirm} onClose={closeDelete}>
        <div
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            background: '#fff',
            padding: '20px',
            borderRadius: '8px',
            width: '420px',
            maxWidth: '90vw',
          }}
        >
          <div style={{ fontWeight: 'bold', fontSize: '16px', marginBottom: '12px' }}>刪除條款</div>
          <div style={{ marginBottom: '16px' }}>
            請確認是否刪除：
            <strong style={{ marginLeft: '6px' }}>
              {menuNode?.control_id ? `${menuNode.control_id} ` : ''}
              {menuNode?.control_name || ''}
            </strong>
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
            <Button variant="outlined" onClick={closeDelete} disabled={deleting}>
              取消
            </Button>
            <Button variant="contained" onClick={confirmDelete} disabled={deleting}>
              {deleting ? '刪除中...' : '確認刪除'}
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
});

export default FSCInlineSingleEditFromTable;
